/**
 * Gère la logique des filtres de la boutique WooCommerce en utilisant AJAX.
 * Utilise la délégation d'événements pour la robustesse lors des rechargements de contenu.
 */
export function initShopFilters() {
    const sidebar = document.querySelector('.shop-sidebar');
    const content = document.querySelector('.shop-content');

    if (!sidebar || !content) {
        return;
    }

    let currentRequestController = null;

    /**
     * Met à jour le contenu de la boutique et les filtres via AJAX.
     * @param {string} url - L'URL à charger.
     */
    const updateShopContent = async (url) => {
        // Fait défiler la vue vers le haut de la liste des produits avec une marge
        const shopContentContainer = document.querySelector('.shop-content');
        if (shopContentContainer) {
            const headerOffset = 4 * parseFloat(getComputedStyle(document.documentElement).fontSize); // 4rem
            const elementPosition = shopContentContainer.getBoundingClientRect().top + window.scrollY;
            const offsetPosition = elementPosition - headerOffset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }

        // Mémoriser les accordéons ouverts
        const openAccordions = [];
        document.querySelectorAll('.filter-accordion-header.active').forEach(header => {
            const key = header.getAttribute('data-accordion');
            if (key) {
                openAccordions.push(key);
            }
        });

        // Annule la requête précédente si elle est en cours
        if (currentRequestController) {
            currentRequestController.abort();
        }
        currentRequestController = new AbortController();
        const signal = currentRequestController.signal;

        try {
            const response = await fetch(url, { signal });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(data, 'text/html');

            const newSidebar = doc.querySelector('.shop-sidebar');
            const newContentHTML = doc.querySelector('.shop-content')?.innerHTML;

            if (newSidebar && newContentHTML) {
                // Met à jour la sidebar
                sidebar.innerHTML = newSidebar.innerHTML;
                
                // Met à jour le contenu des produits
                content.innerHTML = newContentHTML;
                
                // Met à jour l'URL dans la barre d'adresse du navigateur
                window.history.pushState({}, '', url);
                
                // Ré-initialise l'état des accordéons (actifs + ceux mémorisés)
                initializeAllAccordions(openAccordions);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('[ShopFilters] Error during shop update:', error);
            }
        } finally {
            currentRequestController = null;
        }
    };

    /**
     * Construit l'URL à partir des filtres actuellement sélectionnés.
     * @returns {string} L'URL complète avec les paramètres de filtre.
     */
    const buildFilterUrl = () => {
        const form = document.getElementById('shop-filters-form');
        if (!form) return window.location.href;

        const formData = new FormData(form);
        const params = new URLSearchParams();

        // Traiter toutes les entrées du formulaire
        for (const [key, value] of formData.entries()) {
            if (value) { // Ignorer les champs vides
                const name = key.replace('[]', '');
                // Pour les checkboxes, construire une liste
                if (params.has(name)) {
                    params.set(name, params.get(name) + ',' + value);
                } else {
                    params.set(name, value);
                }
            }
        }

        // Ajouter la logique 'OR' pour les attributs multi-sélectionnés
        document.querySelectorAll('.filter-checkbox:checked').forEach(input => {
            const name = input.name.replace('[]', '');
            const values = params.get(name)?.split(',') || [];
            if (name.startsWith('filter_') && values.length > 1) {
                params.set(name.replace('filter_', 'query_type_'), 'or');
            }
        });

        // Conserver le paramètre 'orderby' de l'URL actuelle s'il existe
        const currentUrlParams = new URLSearchParams(window.location.search);
        if (currentUrlParams.has('orderby')) {
            params.set('orderby', currentUrlParams.get('orderby'));
        }

        // Supprimer la pagination pour revenir à la première page
        params.delete('paged');

        let baseUrl = form.action.split('?')[0]; // Nettoyer l'URL de base
        // Supprimer /page/N/ de l'URL pour toujours revenir à la première page lors du filtrage
        baseUrl = baseUrl.replace(/\/page\/\d+\/?$/, '');
        return `${baseUrl}?${params.toString()}`;
    };

    // --- GESTION DES ÉVÉNEMENTS PAR DÉLÉGATION ---

    // Écouteur unifié pour tous les changements dans le formulaire de filtres
    document.body.addEventListener('change', (e) => {
        const target = e.target;

        // Si le changement a lieu dans le formulaire de filtres ou le sélecteur de tri
        if (target.closest('#shop-filters-form') || target.matches('.woocommerce-ordering select.orderby')) {
            const url = buildFilterUrl();
            updateShopContent(url);
        }
    });

    // Écouteur pour les clics (pagination, reset, accordéons, etc.)
    document.body.addEventListener('click', (e) => {
        const target = e.target;
        const ajaxLink = target.closest('.pagination a');
        const clearButton = target.closest('.clear-filters-btn');
        const accordionHeader = target.closest('.filter-accordion-header');
        const filterToggle = target.closest('#filter-toggle');

        if (ajaxLink) {
            e.preventDefault();
            updateShopContent(ajaxLink.href);
        } else if (clearButton) {
            e.preventDefault();
            const form = document.getElementById('shop-filters-form');
            if (form) {
                form.reset();
                const url = buildFilterUrl();
                updateShopContent(url);
            }
        } else if (accordionHeader) {
            toggleAccordion(accordionHeader);
        } else if (filterToggle) {
            const filterContent = sidebar.querySelector('.divide-y, .clear-filters-container');
            filterContent?.parentElement.classList.toggle('hidden-mobile');
        }
    });
    
    // Supprimer l'ancien écouteur de soumission qui n'est plus nécessaire
    // document.body.addEventListener('submit', ...);


    // --- LOGIQUE DE L'ACCORDÉON ---

    const toggleAccordion = (header) => {
        const content = header.nextElementSibling;
        if (!content) return;

        const chevron = header.querySelector('.filter-chevron');
        const isOpen = header.classList.contains('active');

        if (isOpen) {
            content.style.maxHeight = '0px';
            header.classList.remove('active');
            chevron?.classList.remove('rotate-180');
        } else {
            header.classList.add('active'); // Important: ajouter 'active' avant de mesurer scrollHeight
            content.style.maxHeight = content.scrollHeight + 'px';
            chevron?.classList.add('rotate-180');
        }
    };

    const initializeAccordion = (header, previouslyOpen = []) => {
        const content = header.nextElementSibling;
        if (!content) return;

        const key = header.getAttribute('data-accordion');
        
        // Si l'accordéon était ouvert avant le rechargement AJAX, on le ré-ouvre.
        if (previouslyOpen.includes(key)) {
            header.classList.add('active');
            content.style.maxHeight = content.scrollHeight + 'px';
            header.querySelector('.filter-chevron')?.classList.add('rotate-180');
            return; // On s'arrête là pour cet accordéon
        }

        // Logique pour le chargement initial de la page (pas de rechargement AJAX)
        // On ouvre l'accordéon seulement s'il contient un filtre actif.
        const hasActiveFilter = content.querySelector('input:checked') || 
                                (content.querySelector('input[name="min_price"]') && content.querySelector('input[name="min_price"]').value) ||
                                (content.querySelector('input[name="max_price"]') && content.querySelector('input[name="max_price"]').value);

        if (hasActiveFilter) {
            header.classList.add('active');
            content.style.maxHeight = content.scrollHeight + 'px';
            header.querySelector('.filter-chevron')?.classList.add('rotate-180');
        } else {
            content.style.maxHeight = '0px';
            header.classList.remove('active');
            header.querySelector('.filter-chevron')?.classList.remove('rotate-180');
        }
    };
    
    const initializeAllAccordions = (previouslyOpen = []) => {
        document.querySelectorAll('.filter-accordion-header').forEach(header => initializeAccordion(header, previouslyOpen));
    };

    // Initialisation au chargement de la page
    initializeAllAccordions();
}
